'use client';

import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useState,
} from 'react';

export type IssuePageViewer = 'owner' | 'user' | 'public' | 'supporter';

// Define the issue page state type
export type IssuePageState =
  | { mode: 'creating'; hasTopic: false }
  | { mode: 'creating'; hasTopic: true }
  | { mode: 'editing' }
  | { mode: 'previewing'; from: 'creating' | 'editing' }
  | { mode: 'viewing'; viewer: IssuePageViewer }
  | { mode: 'viewing'; viewer: 'public'; modal: 'login' }
  | { mode: 'viewing'; viewer: 'supporter'; modal: 'boostSway' };

// Define the context type
interface IssuePageContextType {
  pageState: IssuePageState;
  setPageState: (state: IssuePageState) => void;
  isCreateModeWithNoInfo: (
    state: IssuePageState,
  ) => state is { mode: 'creating'; hasTopic: false };
  isCreateModeWithRequiredInfo: (
    state: IssuePageState,
  ) => state is { mode: 'creating'; hasTopic: true };
  isEditMode: (state: IssuePageState) => state is { mode: 'editing' };
  isPreviewModeFromCreating: (
    state: IssuePageState,
  ) => state is { mode: 'previewing'; from: 'creating' };
  isPreviewModeFromEditing: (
    state: IssuePageState,
  ) => state is { mode: 'previewing'; from: 'editing' };
  isViewModeAsOwner: (
    state: IssuePageState,
  ) => state is { mode: 'viewing'; viewer: 'owner' };
  isViewModeAsUser: (
    state: IssuePageState,
  ) => state is { mode: 'viewing'; viewer: 'user' };
  isViewModeAsSupporter: (
    state: IssuePageState,
  ) => state is { mode: 'viewing'; viewer: 'supporter' };
  isViewModeAsPublic: (
    state: IssuePageState,
  ) => state is { mode: 'viewing'; viewer: 'public' };
  isViewModeWithLoginModal: (
    state: IssuePageState,
  ) => state is { mode: 'viewing'; viewer: 'public'; modal: 'login' };
  isViewModeWithBoostSwayModal: (
    state: IssuePageState,
  ) => state is { mode: 'viewing'; viewer: 'supporter'; modal: 'boostSway' };
  getIsCreateMode: () => boolean; // This includes all create modes
  getIsFullEditMode: () => boolean; // This includes all edit/creation modes where all fields are editable
  getIsPreviewMode: () => boolean; // This includes all preview modes
  getIsStaticMode: () => boolean; // This includes all preview and view modes
  getIsViewMode: () => boolean; // This includes all view modes
  getIsVoteMode: () => boolean; // This includes all view modes wherein a user is in the voting flow
}

// Create the context with undefined default value
const IssuePageContext = createContext<IssuePageContextType | undefined>(
  undefined,
);

// Create the provider component
export const IssuePageProvider: React.FC<{
  children: ReactNode;
  initialState: IssuePageState;
}> = ({ children, initialState }) => {
  const [pageState, setPageState] = useState<IssuePageState>(initialState);

  const isCreateModeWithNoInfo = useCallback(
    (state: IssuePageState): state is { mode: 'creating'; hasTopic: false } => {
      return state.mode === 'creating' && state.hasTopic === false;
    },
    [],
  );

  const isCreateModeWithRequiredInfo = useCallback(
    (state: IssuePageState): state is { mode: 'creating'; hasTopic: true } => {
      return state.mode === 'creating' && state.hasTopic === true;
    },
    [],
  );

  const isEditMode = useCallback(
    (state: IssuePageState): state is { mode: 'editing' } => {
      return state.mode === 'editing';
    },
    [],
  );

  const isPreviewModeFromCreating = useCallback(
    (
      state: IssuePageState,
    ): state is { mode: 'previewing'; from: 'creating' } => {
      return (
        state.mode === 'previewing' &&
        'from' in state &&
        state.from === 'creating'
      );
    },
    [],
  );

  const isPreviewModeFromEditing = useCallback(
    (
      state: IssuePageState,
    ): state is { mode: 'previewing'; from: 'editing' } => {
      return (
        state.mode === 'previewing' &&
        'from' in state &&
        state.from === 'editing'
      );
    },
    [],
  );

  const isViewModeAsOwner = useCallback(
    (state: IssuePageState): state is { mode: 'viewing'; viewer: 'owner' } => {
      return state.mode === 'viewing' && state.viewer === 'owner';
    },
    [],
  );

  const isViewModeAsUser = useCallback(
    (state: IssuePageState): state is { mode: 'viewing'; viewer: 'user' } => {
      return state.mode === 'viewing' && state.viewer === 'user';
    },
    [],
  );

  const isViewModeAsSupporter = useCallback(
    (
      state: IssuePageState,
    ): state is { mode: 'viewing'; viewer: 'supporter' } => {
      return state.mode === 'viewing' && state.viewer === 'supporter';
    },
    [],
  );

  const isViewModeAsPublic = useCallback(
    (state: IssuePageState): state is { mode: 'viewing'; viewer: 'public' } => {
      return state.mode === 'viewing' && state.viewer === 'public';
    },
    [],
  );

  const isViewModeWithLoginModal = useCallback(
    (
      state: IssuePageState,
    ): state is { mode: 'viewing'; viewer: 'public'; modal: 'login' } => {
      return (
        state.mode === 'viewing' &&
        state.viewer === 'public' &&
        'modal' in state &&
        state.modal === 'login'
      );
    },
    [],
  );

  const isViewModeWithBoostSwayModal = useCallback(
    (
      state: IssuePageState,
    ): state is {
      mode: 'viewing';
      viewer: 'supporter';
      modal: 'boostSway';
    } => {
      return (
        state.mode === 'viewing' &&
        state.viewer === 'supporter' &&
        'modal' in state &&
        state.modal === 'boostSway'
      );
    },
    [],
  );

  const getIsCreateMode = useCallback(
    () =>
      isCreateModeWithNoInfo(pageState) ||
      isCreateModeWithRequiredInfo(pageState),
    [pageState, isCreateModeWithNoInfo, isCreateModeWithRequiredInfo],
  );

  const getIsFullEditMode = useCallback(
    () => isEditMode(pageState) || isCreateModeWithRequiredInfo(pageState),
    [pageState, isEditMode, isCreateModeWithRequiredInfo],
  );

  const getIsPreviewMode = useCallback(
    () =>
      isPreviewModeFromCreating(pageState) ||
      isPreviewModeFromEditing(pageState),
    [pageState, isPreviewModeFromCreating, isPreviewModeFromEditing],
  );

  const getIsStaticMode = useCallback(
    () =>
      isViewModeAsOwner(pageState) ||
      isViewModeAsUser(pageState) ||
      isViewModeAsPublic(pageState) ||
      isViewModeAsSupporter(pageState) ||
      isPreviewModeFromCreating(pageState) ||
      isPreviewModeFromEditing(pageState),
    [
      pageState,
      isViewModeAsOwner,
      isViewModeAsUser,
      isViewModeAsPublic,
      isViewModeAsSupporter,
      isPreviewModeFromCreating,
      isPreviewModeFromEditing,
    ],
  );

  const getIsViewMode = useCallback(
    () =>
      isViewModeAsOwner(pageState) ||
      isViewModeAsUser(pageState) ||
      isViewModeAsSupporter(pageState) ||
      isViewModeAsPublic(pageState),
    [
      pageState,
      isViewModeAsOwner,
      isViewModeAsUser,
      isViewModeAsSupporter,
      isViewModeAsPublic,
    ],
  );

  const getIsVoteMode = useCallback(
    () =>
      isViewModeWithLoginModal(pageState) ||
      isViewModeWithBoostSwayModal(pageState),
    [pageState, isViewModeWithLoginModal, isViewModeWithBoostSwayModal],
  );

  const value = {
    pageState,
    setPageState,
    isCreateModeWithNoInfo,
    isCreateModeWithRequiredInfo,
    isEditMode,
    isPreviewModeFromCreating,
    isPreviewModeFromEditing,
    isViewModeAsOwner,
    isViewModeAsUser,
    isViewModeAsSupporter,
    isViewModeAsPublic,
    isViewModeWithLoginModal,
    isViewModeWithBoostSwayModal,
    getIsCreateMode,
    getIsFullEditMode,
    getIsPreviewMode,
    getIsViewMode,
    getIsStaticMode,
    getIsVoteMode,
  };

  return (
    <IssuePageContext.Provider value={value}>
      {children}
    </IssuePageContext.Provider>
  );
};

// Hook to use the context
export const useIssuePage = () => {
  const context = useContext(IssuePageContext);
  if (!context) {
    throw new Error('useIssuePage must be used within IssuePageProvider');
  }
  return context;
};
